import React, { useState } from 'react';
import { string, node } from 'prop-types';
import { Input, FormController, FormLabel, Typography } from '@one-thd/sui-atomic-components';

const AVERAGE_DOOR_AREA = 21;
const AVERAGE_WINDOW_AREA = 12;
const SQUARE_FEET_PER_GALLON = 400;
const MIN_GALLONS = 2;
const unitOfMeasure = '(ft.)';
const optionalLabel = '(optional)';
const defaultState = {
  height: '',
  length: '',
  width: '',
  doors: '',
  windows: ''
};

const validateInputText = (value) => value && /^0*[1-9]\d*$/.test(value);

export const WallAreaCalculator = ({
  introPane,
  errorMessage,
  disclaimer
}) => {
  const [calculatorState, setCalculatorState] = useState(defaultState);
  const {
    height,
    length,
    width,
    doors,
    windows
  } = calculatorState;
  const totalSquareFeet = ((length * 2) + (width * 2)) * height;
  const getGallons = () => {
    return Math.ceil((totalSquareFeet - (AVERAGE_DOOR_AREA * doors) - (AVERAGE_WINDOW_AREA * windows))
      / SQUARE_FEET_PER_GALLON);
  };

  const gallons = (length && width && height) ? getGallons() : null;
  const amount = gallons < MIN_GALLONS ? '1 Gallon' : `${gallons} Gallons`;
  const doorsText = `${doors || 'no'} door${doors === '1' ? '' : 's'} `;
  const windowsText = `${windows || 'no'} window${windows === '1' ? '' : 's'}`;

  const showError = Object.keys(calculatorState).some((key) => {
    return calculatorState[key] && !validateInputText(calculatorState[key]);
  });
  const showResult = gallons !== null && !showError;

  const getInput = (label, propertyName, propertyValue) => {
    const errorStatus = (propertyValue && !validateInputText(propertyValue)) ? 'error' : null;
    return (
      <div className="sui-mb-3">
        <FormController fullWidth>
          <FormLabel htmlFor={`input-${propertyName}`}>{label}</FormLabel>
          <Input
            id={`input-${propertyName}`}
            type="number"
            fullWidth
            value={propertyValue}
            onChange={(event) => setCalculatorState({ ...calculatorState, [propertyName]: event.target.value })}
            status={errorStatus}
          />
        </FormController>
      </div>
    );
  };

  // @TODO: We need a more generic message to display the results. It's too specific to paint.

  return (
    <div
      className="sui-flex sui-flex-col md:sui-grid md:sui-grid-cols-3 md:sui-gap-5"
      data-component="WallAreaCalculator"
    >
      <div className="sui-w-full">
        <form autoComplete="off" noValidate>
          {getInput(`Height ${unitOfMeasure}`, 'height', height)}
          {getInput(`Width ${unitOfMeasure}`, 'width', width)}
          {getInput(`Length ${unitOfMeasure}`, 'length', length)}
          {getInput(`# of doors ${optionalLabel}`, 'doors', doors)}
          {getInput(`# of windows ${optionalLabel}`, 'windows', windows)}
        </form>
      </div>
      <div className="sui-flex sui-flex-col md:sui-col-span-2">
        <div className="sui-flex sui-bg-subtle sui-basis-full sui-justify-center">
          {!showResult && !showError && introPane && (
            <div className="sui-flex sui-flex-col sui-p-5 sui-text-md sui-items-center sui-text-center
             md:sui-flex-row"
            >
              {introPane}
            </div>
          )}
          {showError && errorMessage && (
            <div className="sui-p-3 sui-m-3">
              <Typography align="center" color="danger">{errorMessage}</Typography>
            </div>
          )}
          {showResult && (
            <div className="sui-flex sui-flex-col sui-p-5 sui-items-center sui-text-center">
              <Typography variant="h5" color="subtle">YOU WILL NEED</Typography>
              <Typography variant="h1" weight="display">{amount}</Typography>
              <Typography align="center" variant="body-lg">
                Based on one coat of paint for a {width}&#8217; x {length}&#8217; room{' '}
                with {doorsText} and {windowsText}
              </Typography>
            </div>
          )}
        </div>
        {showResult && disclaimer && (
          <Typography variant="body-xs">PLEASE NOTE: {disclaimer}</Typography>
        )}
      </div>
    </div>
  );
};

WallAreaCalculator.displayName = 'WallAreaCalculator';

WallAreaCalculator.propTypes = {
  introPane: node.isRequired,
  errorMessage: string.isRequired,
  disclaimer: string
};

WallAreaCalculator.defaultProps = {
  disclaimer: ''
};
